
import { ActionType } from "../../../../models/ActionType";
import { ApplicationConstants } from "../../../../models/ApplicationConstants";
import { Guid } from "../../../../infrastructure/Guid";
import { IProductFamily } from "../../../../models/IProductFamily";
import { IDealState } from "../state/IDealState";
import { isNullOrEmpty } from "../../../../components/shared/common/util";
import { nextGenService } from "../../../../services/NextGenService";

export interface IRelatedPfamSearchOptions {
    indexUrl: string;
    applicableDealValue: number;
    channelType: number;
    visibilityField: string;
    productTypeField?: string;
    productTypeValue?: string;
    top?: number;
}

export interface IRelatedPfamSelection {
    dealId: string;
    product: IProductFamily;
}

export interface IRelatedPfamValidationOptions {
    applicableDealValue: number;
    channelType: number;
    visibilityField: string;
    productTypeField?: string;
    productTypeValue?: string;
    top?: number;
}

export const RELATED_PFAM_FETCH_TOP = 50;

const escapeODataString = (value: string): string => (value || "").replace(/'/g, "''");

export const areNamesEqual = (first: string, second: string): boolean => {
    return (first || "").trim().toLowerCase() === (second || "").trim().toLowerCase();
};

const tokenizePfamName = (pfamName: string): string[] => {
    return (pfamName || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
};

const normalizePfamName = (pfamName: string): string => {
    const compactName = (pfamName || "").trim().toLowerCase().replace(/\s+/g, "");
    return compactName;
};

const buildVariantSearchQuery = (pfamName: string): string => {
    const tokens = tokenizePfamName(pfamName);
    if (tokens.length <= 1) {
        return (pfamName || "").trim();
    }

    // Generic variant search: use base descriptor without assuming any suffix labels.
    return tokens.slice(0, -1).join(" ");
};

const getFirstAvailableStringField = (source: { [key: string]: any }, keys: string[]): string => {
    for (const key of keys) {
        const value = (source?.[key] || "").toString().trim();
        if (!isNullOrEmpty(value)) {
            return value;
        }
    }

    return "";
};

const getProductGroupingCode = (item: { [key: string]: any }): string => {
    return getFirstAvailableStringField(item, ["ProductGroupingCode", "ProductGroupCode", "GroupingCode"]);
};

const getBusinessUnit = (item: { [key: string]: any }): string => {
    return getFirstAvailableStringField(item, ["BusinessUnit", "BusinessUnitName"]);
};

const getSkuId = (item: { [key: string]: any }): string => {
    return getFirstAvailableStringField(item, ["SKUId", "SkuId", "SkuID"]);
};

const buildGroupingKey = (item: { [key: string]: any }): string => {
    const groupingCode = getProductGroupingCode(item).toLowerCase();
    const businessUnit = getBusinessUnit(item).toLowerCase();

    if (isNullOrEmpty(groupingCode) || isNullOrEmpty(businessUnit)) {
        return "";
    }

    return `${groupingCode}|${businessUnit}`;
};

const isPrefixTokenMatch = (shorter: string[], longer: string[]): boolean => {
    if (!shorter.length || shorter.length >= longer.length) {
        return false;
    }

    for (let index = 0; index < shorter.length; index++) {
        if (shorter[index] !== longer[index]) {
            return false;
        }
    }

    return true;
};

const getVariantBaseName = (pfamName: string): string => {
    const tokens = tokenizePfamName(pfamName);
    if (tokens.length <= 1) {
        return "";
    }

    return tokens.slice(0, -1).join(" ");
};

const getCommonPrefixLength = (first: string, second: string): number => {
    const max = Math.min(first.length, second.length);
    let index = 0;
    while (index < max && first.charCodeAt(index) === second.charCodeAt(index)) {
        index++;
    }
    return index;
};

const getCommonSuffixLength = (first: string, second: string): number => {
    const max = Math.min(first.length, second.length);
    let index = 0;
    while (index < max &&
        first.charCodeAt(first.length - 1 - index) === second.charCodeAt(second.length - 1 - index)) {
        index++;
    }
    return index;
};

const areVariantRelatedPfams = (firstName: string, secondName: string): boolean => {
    const firstTokens = tokenizePfamName(firstName);
    const secondTokens = tokenizePfamName(secondName);

    if (!firstTokens.length || !secondTokens.length) {
        return false;
    }

    if (firstTokens.length === secondTokens.length) {
        // Equal-length names must share the same leading base (everything except the last token).
        const firstBase = getVariantBaseName(firstName);
        const secondBase = getVariantBaseName(secondName);
        if (isNullOrEmpty(firstBase) || firstBase !== secondBase) {
            return false;
        }

        const firstLast = firstTokens[firstTokens.length - 1];
        const secondLast = secondTokens[secondTokens.length - 1];
        if (firstLast === secondLast) {
            return false;
        }
        const commonPrefix = getCommonPrefixLength(firstLast, secondLast);
        const commonSuffix = getCommonSuffixLength(firstLast, secondLast);
        const shorterLength = Math.min(firstLast.length, secondLast.length);
        const isOneFullPrefixOfOther = commonPrefix === shorterLength;

        if (!isOneFullPrefixOfOther && commonPrefix > 0 && commonSuffix > 0) {
            return false;
        }

        return true;
    }

    // Different-length names: require a strict token-prefix relationship.
    return firstTokens.length < secondTokens.length
        ? isPrefixTokenMatch(firstTokens, secondTokens)
        : isPrefixTokenMatch(secondTokens, firstTokens);
};

// Upper bound on rows fetched per batched call. Lets a single search return
// every eligible PFAM in the deal's scope (countries × BUs × product type) so
// counterpart matching can run entirely on the FE against one shared response.
const RELATED_PFAM_BATCH_TOP_CAP = 1000;

const buildRelatedPfamFilterExpression = (
    sourceProduct: IProductFamily,
    options: IRelatedPfamSearchOptions
): string => {
    return buildRelatedPfamMultiScopeFilter(
        sourceProduct.countryId ? [sourceProduct.countryId] : [],
        sourceProduct.businessUnitId ? [sourceProduct.businessUnitId] : [],
        options
    );
};

// Builds an OData filter that includes ALL provided countries/BUs at once using
// search.in(...). This lets a single Azure Search call cover every country/BU
// the user selected for the deal. Per-source country/BU matching still runs
// locally inside selectCounterpartsFromResults.
const buildRelatedPfamMultiScopeFilter = (
    countryIds: string[],
    businessUnitIds: string[],
    options: IRelatedPfamSearchOptions
): string => {
    const escapedProductType = escapeODataString(options.productTypeValue || "");

    let filterExpression = `${ApplicationConstants.ProductSearchApplicableDeals}/any(d: d eq ${options.applicableDealValue}) and `;
    filterExpression += `${ApplicationConstants.ProductSearchChannelType} eq ${options.channelType} and `;
    filterExpression += `${options.visibilityField} ne false`;

    const uniqueCountries = Array.from(new Set((countryIds || []).filter(id => !isNullOrEmpty(id))));
    if (uniqueCountries.length === 1) {
        filterExpression += ` and CountryId eq '${escapeODataString(uniqueCountries[0])}'`;
    } else if (uniqueCountries.length > 1) {
        const list = uniqueCountries.map(id => escapeODataString(id)).join(",");
        filterExpression += ` and search.in(CountryId, '${list}', ',')`;
    }

    const uniqueBusinessUnits = Array.from(new Set((businessUnitIds || []).filter(id => !isNullOrEmpty(id))));
    if (uniqueBusinessUnits.length === 1) {
        filterExpression += ` and BusinessUnitId eq '${escapeODataString(uniqueBusinessUnits[0])}'`;
    } else if (uniqueBusinessUnits.length > 1) {
        const list = uniqueBusinessUnits.map(id => escapeODataString(id)).join(",");
        filterExpression += ` and search.in(BusinessUnitId, '${list}', ',')`;
    }

    if (!isNullOrEmpty(options.productTypeField) && !isNullOrEmpty(escapedProductType)) {
        filterExpression += ` and ${options.productTypeField} eq '${escapedProductType}'`;
    }

    return filterExpression;
};

const fetchProductSearchValues = async (
    indexUrl: string,
    filterExpression: string,
    searchQuery: string,
    top: number
): Promise<any[]> => {
    const apiResponse = await nextGenService.getProductSearchResults(
        indexUrl,
        filterExpression,
        searchQuery,
        0,
        [],
        top
    );

    if (apiResponse?.status !== 200) {
        return [];
    }

    const values = (apiResponse.data as { [key: string]: any })?.value;
    return Array.isArray(values) ? values : [];
};

const selectCounterpartsFromResults = (
    sourceProduct: IProductFamily,
    rawSearchResults: any[]
): IProductFamily[] => {
    const sourceName = (sourceProduct.name || "").trim();
    if (isNullOrEmpty(sourceName) || !rawSearchResults.length) {
        return [];
    }

    const scopedResults = rawSearchResults.filter(item => {
        if (isNullOrEmpty(item?.PfamId) || isNullOrEmpty(item?.PfamName)) {
            return false;
        }

        const isCountryMatch = (item?.CountryId || "").toString().toLowerCase() === (sourceProduct.countryId || "").toLowerCase();
        const isBusinessUnitMatch = isNullOrEmpty(sourceProduct.businessUnitId) ||
            (item?.BusinessUnitId || "").toString().toLowerCase() === (sourceProduct.businessUnitId || "").toLowerCase();

        return isCountryMatch && isBusinessUnitMatch;
    });

    const sourceNormalizedName = normalizePfamName(sourceName);
    const sourceGroupResult = scopedResults.find(item => {
        const itemName = (item?.PfamName || "").toString();
        const itemSkuId = getSkuId(item).toLowerCase();
        const sourceSkuId = (sourceProduct.skuId || "").toLowerCase();

        return areNamesEqual(itemName, sourceName) ||
            (!isNullOrEmpty(sourceSkuId) && itemSkuId === sourceSkuId) ||
            normalizePfamName(itemName) === sourceNormalizedName;
    });

    const sourceGroupingCode = getProductGroupingCode(sourceGroupResult || {});
    const sourceBusinessUnit = getBusinessUnit(sourceGroupResult || {}) || (sourceProduct.businessUnitName || "");
    const sourceGroupKey = !isNullOrEmpty(sourceGroupingCode) && !isNullOrEmpty(sourceBusinessUnit)
        ? `${sourceGroupingCode.toLowerCase()}|${sourceBusinessUnit.toLowerCase()}`
        : "";

    const groupSkuSet = new Set<string>();
    const groupedCandidates = scopedResults.filter(item => {
        const groupingKey = buildGroupingKey(item);
        if (isNullOrEmpty(groupingKey) || groupingKey !== sourceGroupKey) {
            return false;
        }

        const skuId = getSkuId(item).toLowerCase();
        if (!isNullOrEmpty(skuId)) {
            groupSkuSet.add(skuId);
        }

        return true;
    });

    const sourceProductFamilyId = (sourceProduct.productFamilyId || "").toLowerCase();
    const sourceSkuId = (sourceProduct.skuId || "").toLowerCase();
    const isSameSourceResult = (item: { [key: string]: any }): boolean => {
        const itemProductFamilyId = (item?.PfamId || "").toString().toLowerCase();
        const itemSkuId = getSkuId(item).toLowerCase();

        return (!isNullOrEmpty(sourceProductFamilyId) && itemProductFamilyId === sourceProductFamilyId) ||
            (!isNullOrEmpty(sourceSkuId) && itemSkuId === sourceSkuId) ||
            areNamesEqual((item?.PfamName || "").toString(), sourceName);
    };

    const matchingResults = (sourceGroupKey && groupSkuSet.size > 1)
        // Same BusinessUnit + same ProductGroupingCode is the authoritative signal that
        // these PFams are siblings of the source (e.g. SrfcLpt5 13in i5/16/256 CM W10 vs W11).
        // Trust the grouping code here and include all non-source group members regardless of
        // name shape. Name-based variant filtering only applies when the source has no grouping
        // code (handled by the fallback branch below).
        ? groupedCandidates.filter(item => !isSameSourceResult(item))
        : scopedResults.filter(item => {
            const isNotSameName = !isSameSourceResult(item);
            const normalizedNameMatch = normalizePfamName((item?.PfamName || "").toString()) === sourceNormalizedName;
            const isVariantRelated = normalizedNameMatch || areVariantRelatedPfams((item?.PfamName || "").toString(), sourceName);
            return isNotSameName && isVariantRelated;
        });

    return matchingResults.map(matchedResult => ({
        id: Guid.newGuid(),
        countryId: sourceProduct.countryId,
        productFamilyId: matchedResult.PfamId,
        productFamilyCode: matchedResult.ProductFamilyCode || "",
        country: sourceProduct.country,
        name: matchedResult.PfamName || "",
        skuId: getSkuId(matchedResult) || sourceProduct.skuId,
        businessUnitId: matchedResult.BusinessUnitId || sourceProduct.businessUnitId,
        businessUnitName: matchedResult.BusinessUnit || sourceProduct.businessUnitName,
        businessunit: matchedResult.BusinessUnit || sourceProduct.businessUnitName,
        businessUnit: matchedResult.BusinessUnit || sourceProduct.businessUnitName,
        action: ActionType.Add,
        quantity: sourceProduct.quantity || 0,
        applicableDeals: Array.isArray(matchedResult.ApplicableDealsCollection)
            ? matchedResult.ApplicableDealsCollection.join(",")
            : (matchedResult.ApplicableDealsCollection || ""),
    } as IProductFamily));
};

export const getMissingRelatedPfams = async (
    deals: IDealState[],
    options: IRelatedPfamValidationOptions
): Promise<IRelatedPfamSelection[]> => {
    try {
        return await getMissingRelatedPfamsInternal(deals, options);
    } catch (error) {
        // Validation is best-effort: never block submit because of an Azure Search hiccup.
        console.error("[related-pfam] getMissingRelatedPfams failed; proceeding without validation", error);
        return [];
    }
};

const getMissingRelatedPfamsInternal = async (
    deals: IDealState[],
    options: IRelatedPfamValidationOptions
): Promise<IRelatedPfamSelection[]> => {
    const resolved: IRelatedPfamSelection[] = [];
    const indexUrl = (process.env.REACT_APP_SEARCH_API_URL_ENV || "") + ApplicationConstants.ProductSearchIndexApi_Url;

    for (const deal of deals || []) {
        if (!deal.id) {
            continue;
        }

        const dealId = deal.id;
        const selectedProducts = deal.products?.filter(product => product.action?.toLowerCase() !== ActionType.Delete.toLowerCase()) || [];
        if (!selectedProducts.length) {
            continue;
        }

        const productsByCountry = new Map<string, IProductFamily[]>();
        selectedProducts.forEach((product) => {
            const key = (product.countryId || "").toLowerCase();
            const list = productsByCountry.get(key) || [];
            list.push(product);
            productsByCountry.set(key, list);
        });

        // Only validate related PFAMs for newly-added products in this submit.
        // Newly added products (in both create and edit flows) are stamped with
        // action === Add by the product picker, so this filter alone is the right
        // signal: if the user only edited contacts/channel/etc. and added zero
        // new PFAMs, candidates will be empty and no Azure Search calls fire.
        const candidates = selectedProducts.filter(product =>
            product.action?.toLowerCase() === ActionType.Add.toLowerCase() && !isNullOrEmpty(product.name)
        );
        if (!candidates.length) {
            continue;
        }

        const fetchOptions: IRelatedPfamSearchOptions = {
            indexUrl,
            applicableDealValue: options.applicableDealValue,
            channelType: options.channelType,
            visibilityField: options.visibilityField,
            productTypeField: options.productTypeField,
            productTypeValue: options.productTypeValue,
            top: options.top || RELATED_PFAM_FETCH_TOP,
        };

        // Single Azure Search call for the entire deal. The OData filter scopes the
        // result set to the deal's countries/BUs/product type/applicable deal/channel,
        // and we use a wildcard search to retrieve every eligible row in that scope.
        // All counterpart matching (variant name, grouping code, source-self exclusion,
        // already-on-case) runs locally per source against this shared response.
        const countryIds = candidates.map(c => c.countryId || "");
        const businessUnitIds = candidates.map(c => c.businessUnitId || "");
        const filterExpression = buildRelatedPfamMultiScopeFilter(countryIds, businessUnitIds, fetchOptions);

        let rawResults: any[] = [];
        try {
            rawResults = await fetchProductSearchValues(
                fetchOptions.indexUrl,
                filterExpression,
                "*",
                RELATED_PFAM_BATCH_TOP_CAP
            );
        } catch (error) {
            console.error("[related-pfam] batched search failed for deal", dealId, error);
            rawResults = [];
        }

        if (rawResults.length >= RELATED_PFAM_BATCH_TOP_CAP) {
            console.warn(
                "[related-pfam] result set hit the top cap; some siblings may be missing",
                { dealId, top: RELATED_PFAM_BATCH_TOP_CAP }
            );
        }

        const counterpartResults = candidates.map(product => {
            const counterpartProducts = selectCounterpartsFromResults(product, rawResults);
            return { product, counterpartProducts };
        });

        for (const { product, counterpartProducts } of counterpartResults) {
            const countryProducts = productsByCountry.get((product.countryId || "").toLowerCase()) || [];

            for (const counterpart of counterpartProducts) {
                const alreadySelected = countryProducts.some(item => areNamesEqual(item.name || "", counterpart.name || ""));
                if (alreadySelected) {
                    continue;
                }

                resolved.push({
                    dealId,
                    product: counterpart as IProductFamily,
                });
            }
        }
    }

    return Array.from(new Map(resolved.map(item => [`${item.dealId}-${item.product.countryId}-${item.product.productFamilyId}`.toLowerCase(), item])).values());
};

export const fetchRelatedVariantPfams = async (
    sourceProduct: IProductFamily,
    options: IRelatedPfamSearchOptions
): Promise<IProductFamily[]> => {
    if (isNullOrEmpty(sourceProduct?.name)) {
        return [];
    }

    const sourceName = (sourceProduct.name || "").trim();
    if (isNullOrEmpty(sourceName)) {
        return [];
    }

    const searchQuery = buildVariantSearchQuery(sourceName);
    const filterExpression = buildRelatedPfamFilterExpression(sourceProduct, options);
    const rawResults = await fetchProductSearchValues(
        options.indexUrl,
        filterExpression,
        searchQuery,
        options.top || RELATED_PFAM_FETCH_TOP
    );

    return selectCounterpartsFromResults(sourceProduct, rawResults);
};
